import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { filterUnopenedCoachReviewTranscripts } from "@/lib/coachReviewArtifacts";
import { COACH_REVIEWABLE_ANALYSIS_STATUSES, isTranscriptNeedsCoachReview } from "@/lib/transcriptStatus";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

type PendingTranscriptRow = {
  id: string;
  leader_id: string;
  analysis_status: string | null;
  coach_verified: boolean | null;
};

type OpenedTranscriptRow = {
  leader_id: string;
  transcript_upload_id: string | null;
};

type PendingTranscriptReviewSummary = {
  leadersWithPendingTranscriptReviewCount: number;
  pendingTranscriptCountByLeaderId: Record<string, number>;
};

export async function fetchCoachPendingTranscriptReviewSummary(
  userId: string,
  dbClient: typeof db = db,
): Promise<PendingTranscriptReviewSummary> {
  const [linksResult, legacyResult] = await Promise.all([
    dbClient.from("coach_leader_links").select("leader_id").eq("coach_id", userId),
    dbClient.from("profiles").select("id").eq("coach_id", userId),
  ]);

  const idSet = new Set<string>();
  ((linksResult.data || []) as { leader_id: string }[]).forEach((row) => idSet.add(row.leader_id));
  ((legacyResult.data || []) as { id: string }[]).forEach((row) => idSet.add(row.id));
  const leaderIds = Array.from(idSet);

  if (leaderIds.length === 0) {
    return {
      leadersWithPendingTranscriptReviewCount: 0,
      pendingTranscriptCountByLeaderId: {},
    };
  }

  const [transcriptsResult, openedResult] = await Promise.all([
    dbClient
      .from("transcript_uploads")
      .select("id, leader_id, analysis_status, coach_verified")
      .in("leader_id", leaderIds)
      .in("analysis_status", [...COACH_REVIEWABLE_ANALYSIS_STATUSES])
      .or("coach_verified.is.null,coach_verified.eq.false"),
    dbClient
      .from("coach_review_artifact_views")
      .select("leader_id, transcript_upload_id")
      .eq("coach_id", userId)
      .eq("artifact_type", "transcript_review")
      .in("leader_id", leaderIds),
  ]);

  const openedTranscriptIds = new Set(
    ((openedResult.data || []) as OpenedTranscriptRow[])
      .map((row) => row.transcript_upload_id)
      .filter((value): value is string => Boolean(value)),
  );

  const pendingTranscripts = filterUnopenedCoachReviewTranscripts(
    ((transcriptsResult.data || []) as PendingTranscriptRow[]).filter((row) =>
      isTranscriptNeedsCoachReview(row.analysis_status, row.coach_verified),
    ),
    openedTranscriptIds,
  );

  const pendingTranscriptCountByLeaderId = pendingTranscripts.reduce<Record<string, number>>((acc, transcript) => {
    acc[transcript.leader_id] = (acc[transcript.leader_id] || 0) + 1;
    return acc;
  }, {});

  return {
    leadersWithPendingTranscriptReviewCount: Object.keys(pendingTranscriptCountByLeaderId).length,
    pendingTranscriptCountByLeaderId,
  };
}

export function useCoachPendingTranscriptReviewCount() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["coach-pending-transcript-review-count", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      if (!user?.id) {
        return {
          leadersWithPendingTranscriptReviewCount: 0,
          pendingTranscriptCountByLeaderId: {},
        };
      }
      return fetchCoachPendingTranscriptReviewSummary(user.id, db);
    },
    staleTime: 30_000,
  });

  return useMemo(
    () => ({
      leadersWithPendingTranscriptReviewCount: query.data?.leadersWithPendingTranscriptReviewCount || 0,
      pendingTranscriptCountByLeaderId: query.data?.pendingTranscriptCountByLeaderId || {},
      isLoadingPendingTranscriptReviewCount: query.isLoading,
      refetchPendingTranscriptReviewCount: query.refetch,
    }),
    [query.data, query.isLoading, query.refetch],
  );
}
